import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { ShieldCheck, ShieldAlert, KeyRound, CheckCircle2, Fingerprint, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { registerPasskey } from "../lib/webauthn";

export default function Profile() {
  const { user, setUser } = useAuth();
  const [setup, setSetup] = useState(null); // { secret, otpauth_url, qr_png_base64 }
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState([]);
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true);
    try {
      // Backend renders the QR locally (totp-rs qr feature). Never send the
      // otpauth URL to a third party — it contains the TOTP secret.
      const { data } = await api.post("/twofa/enroll");
      setSetup({
        secret: data.secret,
        otpauth_url: data.otpauth_url,
        qr_png_base64: data.qr_png_base64,
      });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const enable = async () => {
    setBusy(true);
    try {
      await api.post("/twofa/confirm", { code });
      toast.success("2FA enabled");
      setRecovery([]); // backend doesn't issue recovery codes today
      setSetup(null); setCode("");
      const me = await api.get("/auth/me"); setUser(me.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await api.post("/twofa/disable");
      toast.success("2FA disabled");
      setCode("");
      const me = await api.get("/auth/me"); setUser(me.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Layout title="Operator Profile" subtitle="ACCOUNT SECURITY">
      <div className="grid grid-cols-12 gap-4">
        {/* Account card */}
        <div className="col-span-12 lg:col-span-5 border border-zinc-800 rounded-sm bg-zinc-900/60 p-5 fade-up">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400 mb-3">// IDENTITY</div>
          <div className="flex items-center gap-3 mb-4">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-12 h-12 rounded-sm border border-zinc-800" />
            ) : (
              <div className="w-12 h-12 rounded-sm border border-zinc-800 bg-zinc-900 grid place-items-center">
                <ShieldCheck className="w-5 h-5 text-cyan-400" />
              </div>
            )}
            <div>
              <div className="font-display text-lg font-bold">{user?.name}</div>
              <div className="text-[11px] font-mono text-zinc-400">{user?.email}</div>
            </div>
          </div>
          <div className="space-y-2 font-mono text-[11px]">
            <Row k="role" v={user?.role} />
            <Row k="auth_provider" v={user?.auth_provider} />
            <Row k="user_id" v={user?.user_id} />
            <Row k="created_at" v={user?.created_at?.slice(0, 19).replace("T", " ")} />
            <Row k="2fa" v={user?.twofa_enabled ? "ENABLED" : "OFF"} hl={user?.twofa_enabled} />
          </div>
        </div>

        {/* 2FA card */}
        <div className="col-span-12 lg:col-span-7 border border-zinc-800 rounded-sm bg-zinc-900/60 p-5 fade-up beam-border">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400 mb-3 flex items-center gap-2">
            <KeyRound className="w-3.5 h-3.5" /> // TWO-FACTOR AUTHENTICATION (TOTP)
          </div>

          {!user?.twofa_enabled && !setup && (
            <div className="space-y-3">
              <p className="text-zinc-400 text-sm">
                Add an extra layer with TOTP (Google Authenticator, 1Password, Authy, Bitwarden).
                When enabled, login requires your password plus a 6-digit rotating code.
              </p>
              <Button data-testid="2fa-start" disabled={busy} onClick={start} className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm">
                ENABLE 2FA
              </Button>
            </div>
          )}

          {setup && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">SCAN QR</div>
                  <img data-testid="2fa-qr" src={`data:image/png;base64,${setup.qr_png_base64}`} alt="QR" className="w-44 h-44 bg-white p-2 rounded-sm" />
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">OR ENTER MANUALLY</div>
                    <code className="block text-xs font-mono text-cyan-400 bg-zinc-950 border border-zinc-800 px-2 py-1.5 rounded-sm break-all" data-testid="2fa-secret">{setup.secret}</code>
                  </div>
                  <div>
                    <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">Verify with current 6-digit code</Label>
                    <Input data-testid="2fa-code" value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono text-lg tracking-widest text-center" placeholder="000000" />
                  </div>
                  <Button data-testid="2fa-confirm" onClick={enable} disabled={busy || code.length !== 6} className="w-full bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm">CONFIRM & ENABLE</Button>
                </div>
              </div>
            </div>
          )}

          {recovery.length > 0 && (
            <div className="mt-4 border border-amber-500/40 bg-amber-500/5 rounded-sm p-4" data-testid="2fa-recovery">
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-amber-400 mb-2 flex items-center gap-2">
                <ShieldAlert className="w-3.5 h-3.5" /> RECOVERY CODES — SAVE THESE NOW
              </div>
              <div className="grid grid-cols-4 gap-2 font-mono text-sm">
                {recovery.map((r) => <code key={r} className="bg-zinc-950 border border-zinc-800 px-2 py-1 rounded-sm text-amber-300 text-center">{r}</code>)}
              </div>
            </div>
          )}

          {user?.twofa_enabled && !setup && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-emerald-400 font-mono text-sm">
                <CheckCircle2 className="w-4 h-4" /> 2FA is ACTIVE
              </div>
              <p className="text-zinc-400 text-sm">To disable, enter a current TOTP code below.</p>
              <Input data-testid="2fa-disable-code" value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono text-lg tracking-widest text-center max-w-xs" placeholder="000000" />
              <Button data-testid="2fa-disable" onClick={disable} disabled={busy || code.length !== 6} variant="ghost" className="text-red-400 hover:text-red-300">DISABLE 2FA</Button>
            </div>
          )}
        </div>

        {/* Passkey card — phishing-resistant factor (TouchID, FaceID, YubiKey). */}
        <div className="col-span-12 border border-zinc-800 rounded-sm bg-zinc-900/60 p-5 fade-up">
          <PasskeysPanel />
        </div>
      </div>
    </Layout>
  );
}

function PasskeysPanel() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const supported = typeof window !== "undefined" && !!window.PublicKeyCredential;

  const load = async () => {
    try { const { data } = await api.get("/webauthn/credentials"); setItems(Array.isArray(data) ? data : []); }
    catch { setItems([]); }
  };
  useEffect(() => { load(); }, []);

  const enroll = async () => {
    if (!name.trim()) { toast.error("Give the passkey a name (e.g. 'MacBook TouchID')"); return; }
    setBusy(true);
    try {
      await registerPasskey(name.trim());
      toast.success("Passkey enrolled");
      setName("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message || "Enrollment failed");
    } finally { setBusy(false); }
  };

  const remove = async (cred_id) => {
    if (!window.confirm("Remove this passkey? You won't be able to log in with it again.")) return;
    try {
      await api.delete(`/webauthn/credentials/${encodeURIComponent(cred_id)}`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <>
      <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400 mb-3 flex items-center gap-2">
        <Fingerprint className="w-3.5 h-3.5" /> // PASSKEYS (WebAuthn)
      </div>
      {!supported && (
        <div className="text-amber-400 font-mono text-xs mb-3">
          This browser doesn't support WebAuthn — try a modern Chrome / Safari / Firefox.
        </div>
      )}
      <p className="text-zinc-400 text-sm mb-3">
        Phishing-resistant. TouchID, FaceID, Windows Hello, or a hardware key (YubiKey).
        You can enroll multiple — useful for backup devices.
      </p>
      <div className="flex items-end gap-3 mb-4">
        <div className="flex-1">
          <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">Passkey name</Label>
          <Input data-testid="passkey-name" placeholder="MacBook TouchID" value={name} onChange={(e) => setName(e.target.value)} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" />
        </div>
        <Button data-testid="passkey-enroll" disabled={busy || !supported || !name.trim()} onClick={enroll} className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm">ENROLL PASSKEY</Button>
      </div>
      {items.length > 0 ? (
        <div className="space-y-2">
          {items.map((c) => (
            <div key={c.cred_id} data-testid={`passkey-${c.cred_id}`} className="flex items-center justify-between border border-zinc-800 rounded-sm px-3 py-2 bg-zinc-950/40">
              <div className="min-w-0">
                <div className="font-mono text-sm text-zinc-200 whitespace-normal break-words">{c.name || "passkey"}</div>
                <div className="font-mono text-[10px] text-zinc-500 whitespace-normal break-words">{c.cred_id} · enrolled {c.created_at?.slice(0, 19).replace("T", " ")}</div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => remove(c.cred_id)} className="text-red-400 hover:text-red-300 gap-1.5">
                <Trash2 className="w-3.5 h-3.5" /> Remove
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-zinc-500 font-mono text-sm">no passkeys enrolled.</div>
      )}
    </>
  );
}

function Row({ k, v, hl }) {
  return (
    <div className="flex justify-between border-b border-zinc-800/60 pb-1.5">
      <span className="text-zinc-500 uppercase tracking-widest text-[10px]">{k}</span>
      <span className={hl ? "text-emerald-400" : "text-zinc-200"}>{String(v ?? "—")}</span>
    </div>
  );
}
